import { useCallback, useEffect, useState } from 'react';
import { useSetImmerAtom } from 'jotai-immer';
import { ngContext } from '../../../../core/services/ng.delegate';
import { feedsPageStateAtom, feedsUserContextAtom } from '../feeds_list_atoms';
import { useFeedsListDestroy } from '../table/useFeedsListDestroy';
import { PreloadState } from '../../../../core/page/page.types';
import utilSvc from '../../../../core/utilSvc';
import { getFeedTypeOptions, getStatusOptions, getSourceOptions } from '../../data/feedsRepo';
import { ValidOption } from '../../../../core/components/validation/types';
import { getDefaultStore } from 'jotai';

/**
 * Determine the UI mode based on current route and user features
 * Matches legacy logic from feedsCtl.init():
 * - If route is 'ops.datamonitor':
 *   - If user has 'report.wacneut' feature: 'CDC' mode
 *   - Otherwise: 'CE' mode
 * - Otherwise: 'VERITY' mode
 */
export function getUiModeForFeeds(): string {
    const ctx = ngContext();
    let userType = ctx.User?.Type ?? 'CE';

    if (userType === 'CE') {
        userType = utilSvc.user.hasFeature(ctx.User, 'report.wacneut') ? 'CDC' : 'CE';
    } else {
        userType = 'VERITY';
    }
    return userType;
}

type DropdownOptionsReturn = {
    facilityOptions: ValidOption[];
    pharmacyOptions: ValidOption[];
    feedTypeOptions: ValidOption[];
    statusOptions: ValidOption[];
    sourceOptions: ValidOption[];
};

export function buildDropdownOptions(ctx: any): DropdownOptionsReturn {
    // Get affiliations from user context
    const affs = utilSvc.aff.findStatus(ctx.User?.AffList ?? []);

    // Determine UI mode based on current route and user features
    const uiMode = getUiModeForFeeds();

    // Build facility and pharmacy options
    const facilityList = utilSvc.aff.ceList(affs);
    const pharmacyList = utilSvc.aff.rxList(affs);

    // Convert to option format: { lbl: string, val: string | number }
    const facilityOptions = facilityList.map(ce => ({ lbl: ce, val: ce }));
    const pharmacyOptions = pharmacyList.map(rx => ({ lbl: rx, val: rx }));

    // Get feed type options based on UI mode
    const feedTypeList = getFeedTypeOptions(uiMode);
    const feedTypeOptions = feedTypeList.map(ft => ({ lbl: ft, val: ft }));

    // Get status options (same across all UI modes)
    const statusList = getStatusOptions();
    const statusOptions = statusList.map(st => ({ lbl: st, val: st }));

    // Get source options (same across all UI modes)
    const sourceList = getSourceOptions();
    const sourceOptions = sourceList.map(src => ({ lbl: src, val: src }));

    return {
        facilityOptions,
        pharmacyOptions,
        feedTypeOptions,
        statusOptions,
        sourceOptions,
    };
}

export const useFeedsInit = (): boolean => {
    const [preloadState, setPreloadState] = useState<PreloadState>('init');
    const setPageState = useSetImmerAtom(feedsPageStateAtom);

    useFeedsListDestroy();

    const preloadPage = useCallback(async () => {
        const ctx = ngContext();

        // Populate the user context atom once at page init — all sub-components read from here
        const defaultStore = getDefaultStore();
        defaultStore.set(feedsUserContextAtom, {
            userType: ctx.User?.Type || 'CE',
            isImpersonating: ctx.User?.Impersonate || false,
            affiliations: ctx.User?.AffList ?? [],
        });

        setPageState(draft => {
            draft.canEdit = !!ctx && !!ctx.User;
            draft.init = false;
            draft.uiMode = getUiModeForFeeds();
            draft.loading = false;
            draft.action = 'QUERY'; // advance page to QUERY mode
            return draft;
        });

        setPreloadState('done');
    }, [setPageState]);

    useEffect(() => {
        if (preloadState === 'init') {
            setPreloadState('loading');
            preloadPage();
        }
    }, [preloadState, setPreloadState, preloadPage]);

    return preloadState === 'done';
};
